import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Globals } from "../../app.globals";

import { AbstractService } from "../../abstractions/abstract-typed.service";
import { Observable } from "rxjs";
import { catchError, finalize, map } from "rxjs/operators";

@Injectable()
export class LabelPreparationService extends AbstractService<LabelPreparationSetup> {
    formArea: string;
    formEntryNumber: number;

    constructor(public http: HttpClient, public globals: Globals) {
        super(http, globals);
    }

    getApiUrl() {
        return "/api/settings/";
    }

    add(labelPreparationSetup: LabelPreparationSetup): Observable<number> {
        this.showSpinner();
        return this.http
            .post(
                this.getApiUrl() +
                "labelpreparation/" +
                this.formArea + "/" +
                this.formEntryNumber + "/" +
                "add/",
                labelPreparationSetup
            )
            .pipe(
                map((res) => res),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    edit(labelPreparationSetup: LabelPreparationSetup): Observable<any> {
        this.showSpinner();
        return this.http
            .post(
                this.getApiUrl() +
                "labelpreparation/" +
                this.formArea + "/" +
                this.formEntryNumber + "/" +
                "edit",
                labelPreparationSetup
            )
            .pipe(
                map((res) => res),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    remove(entryNumber: number) {
        return this.http
            .delete(
                this.getApiUrl() +
                "labelpreparation/" +
                this.formArea + "/" +
                this.formEntryNumber + "/" +
                "remove/" +
                entryNumber
            )
            .pipe(
                catchError(this.handleError)
            );
    }

    activate(entryNumber: number) {
        return this.http
            .put(
                this.getApiUrl() +
                "labelpreparation/" +
                this.formArea + "/" +
                this.formEntryNumber + "/" +
                "activate/" +
                entryNumber,
                null
            )
            .pipe(
                catchError(this.handleError)
            );
    }

    deactivate(entryNumber: number) {
        return this.http
            .put(
                this.getApiUrl() +
                "labelpreparation/" +
                this.formArea + "/" +
                this.formEntryNumber + "/" +
                "deactivate/" +
                entryNumber,
                null
            )
            .pipe(
                catchError(this.handleError)
            );
    }

    getAll() {
        return this.http
            .get(
                this.getApiUrl() +
                "labelpreparation/" +
                this.formArea + "/" +
                this.formEntryNumber + "/" +
                "getall/"
            )
            .pipe(
                map((res) => res),
                catchError(this.handleError)
            );
    }

    getAllActive() {
        return this.http
            .get(
                this.getApiUrl() +
                "labelpreparation/" +
                this.formArea + "/" +
                this.formEntryNumber + "/" +
                "getallactive/"
            )
            .pipe(
                map((res) => res),
                catchError(this.handleError)
            );
    }

    export(
        payload: any,
        labelPreparationEntryNumber: number
    ): Observable<any> {
        this.showSpinner()
        return this.http
            .post(
                this.getApiUrl() +
                "labelpreparation/" +
                this.formArea + "/" +
                this.formEntryNumber + "/" +
                "export/" +
                labelPreparationEntryNumber,
                payload,
                { responseType: 'blob' as 'json' }
            )
            .pipe(
                map((res) => res),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }
}

export class LabelPreparationSetup {
    id: number;
    formId: number;
    entryNumber: number;
    name: string;
    isActive: boolean;
    isDeleted: boolean;
    createdBy: string;
    createdById: number;
    created: Date;
    modifiedBy: string;
    modifiedById: number;
    modified: Date;
    labelPreparationSetupExportFormQuestions: LabelPreparationSetupExportFormQuestion[];
    labelPreparationSetupExportQRCodes: LabelPreparationSetupExportQRCode[];

    constructor() {
        this.id = 0;
        this.formId = 0;
        this.entryNumber = 0;
        this.name = "";
        this.isActive = false;
        this.isDeleted = false;
        this.createdBy = null;
        this.createdById = 0;
        this.created = new Date();
        this.modifiedBy = null;
        this.modifiedById = 0;
        this.modified = null;
        this.labelPreparationSetupExportFormQuestions = [];
        this.labelPreparationSetupExportQRCodes = [];
    }

    static map(value: any): LabelPreparationSetup {
        if (!value) return new LabelPreparationSetup();
        return {
            id: value.id,
            formId: value.formId,
            entryNumber: value.entryNumber,
            name: value.name,
            isActive: value.isActive,
            isDeleted: value.isDeleted,
            modifiedBy: value.modifiedBy,
            modifiedById: value.modifiedById,
            modified: value.modified,
            createdBy: value.createdBy,
            createdById: value.createdById,
            created: value.created,
            labelPreparationSetupExportFormQuestions: value.labelPreparationSetupExportFormQuestions,
            labelPreparationSetupExportQRCodes: value.labelPreparationSetupExportQRCodes,
        };
    }
}

export class LabelPreparationSetupExportQRCode {
    id: number;
    labelPreparationSetupId: number;
    qrCodeSetupId: number;
    order: number;

    constructor() {
        this.id = 0;
        this.labelPreparationSetupId = 0;
        this.qrCodeSetupId = 0;
        this.order = 0;
    }

    static map(value: any): LabelPreparationSetupExportQRCode {
        if (!value) return new LabelPreparationSetupExportQRCode();
        return {
            id: value.id,
            labelPreparationSetupId: value.labelPreparationSetupId,
            qrCodeSetupId: value.qrCodeSetupId,
            order: value.order,
        }
    }
}

export class LabelPreparationSetupExportFormQuestion {
    id: number;
    labelPreparationSetupId: number;
    questionId: number;
    order: number;

    constructor() {
        this.id = 0;
        this.labelPreparationSetupId = 0;
        this.questionId = 0;
        this.order = 0;
    }

    static map(value: any): LabelPreparationSetupExportFormQuestion {
        if (!value) return new LabelPreparationSetupExportFormQuestion();
        return {
            id: value.id,
            labelPreparationSetupId: value.labelPreparationSetupId,
            questionId: value.questionId,
            order: value.order,
        }
    }
}